<template>
    <div fluid class="slide-treatment-filter">
        <div class="close-label" @click="toggleTreatmentFilter">{{ $translate('close') }}</div>

        <div class="divider" />
        <div class="treatment-container">
            <div class="treatment-methods">
                <b-form>
                    <div class="treatment-input">
                        <div class="treatment-input-label">
                            {{ $translate('collected_oocytes') }}
                        </div>
                        <div class="square-input-container">
                            <b-form-input
                                id="collectedOocytes"
                                v-model="form.collectedOocytes"
                                type="number"
                                class="square-input"
                                placeholder="-"
                                lazy-formatter
                                @blur="updateTreatmentDetails('collectedOocytes')"
                            />
                        </div>
                    </div>

                    <div class="treatment-input">
                        <div class="treatment-input-label">{{ $translate('mii') }}</div>
                        <div class="square-input-container">
                            <b-form-input
                                id="mii"
                                v-model="form.mii"
                                type="number"
                                class="square-input"
                                placeholder="-"
                                lazy-formatter
                                @blur="updateTreatmentDetails('mii')"
                            />
                        </div>
                    </div>

                    <div class="treatment-input">
                        <div class="treatment-input-label">
                            {{ $translate('inseminated_ICSI') }}
                        </div>
                        <div class="square-input-container">
                            <b-form-input
                                id="inseminatedICSI"
                                v-model="form.inseminatedICSI"
                                type="number"
                                class="square-input"
                                placeholder="-"
                                lazy-formatter
                                @blur="updateTreatmentDetails('inseminatedICSI')"
                            />
                        </div>
                    </div>

                    <div class="treatment-input">
                        <div class="treatment-input-label">{{ $translate('inseminated_ivf') }}</div>
                        <div class="square-input-container">
                            <b-form-input
                                id="inseminatedIVF"
                                v-model="form.inseminatedIVF"
                                type="number"
                                class="square-input"
                                placeholder="-"
                                lazy-formatter
                                @blur="updateTreatmentDetails('inseminatedIVF')"
                            />
                        </div>
                    </div>

                    <div class="treatment-input">
                        <div class="treatment-input-label">{{ $translate('degenerated') }}</div>
                        <div class="square-input-container">
                            <b-form-input
                                id="degenerated"
                                v-model="form.degenerated"
                                type="number"
                                class="square-input"
                                placeholder="-"
                                lazy-formatter
                                @blur="updateTreatmentDetails('degenerated')"
                            />
                        </div>
                    </div>
                </b-form>
            </div>
        </div>
        <div class="insemination-container">
            <div v-if="!fertMethodNeedsApproval" class="slides-approved">
                All slide insemination are updated
            </div>
            <div v-else>
                <div class="insemination-method">Insemination Method</div>

                <div class="show-slide">
                    <div class="slideId">
                        {{ selectedSlide && selectedSlide.esId }}
                        <fert-method-dropdown default-label="select" />
                    </div>
                    <div class="wells-count">
                        {{ activeSlideNonEmptyWells.length }}
                        <span> {{ $translate('wells') }} </span>
                    </div>
                </div>
            </div>

            <div class="insemination-instruction">
                *Insemination method can be edited for individual wells
            </div>

            <div v-if="treatment.slides.length > 1 && fertMethodNeedsApproval" class="indicator">
                <div
                    v-for="slide in treatment.slides"
                    :key="slide._id"
                    class="slide-indicators text-align"
                    :class="{ currentSlide: isSelected(slide._id) }"
                    @click="selectSlide(slide)"
                />
            </div>

            <div
                v-if="fertMethodNeedsApproval"
                class="approve-btn"
                @click="handleApproveButtonClick"
            >
                <b-button
                    variant="primary"
                    :disabled="!!isSlideApproved || noEmbryosHaveFertMethod"
                >
                    {{ $translate('approve') }}
                </b-button>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import FertMethodDropdown from './common/FertMethodDropdown.vue'

export default {
    components: { FertMethodDropdown },
    data() {
        return {
            form: {
                collectedOocytes: '',
                mii: '',
                inseminatedICSI: '',
                inseminatedIVF: '',
                degenerated: ''
            }
        }
    },
    computed: {
        ...mapState({
            treatment: state => state.timelapse.selectedTreatment,
            selectedSlide: state => state.timelapse.selectedSlide,
            cycleDetailsAreVisible: state => state.filter.cycleDetailsAreVisible
        }),
        ...mapGetters({
            tenant: 'common/getTenant',
            activeSlideNonEmptyWells: 'timelapse/activeSlideNonEmptyWells',
            fertMethodNeedsApproval: 'timelapse/fertMethodNeedsApproval'
        }),
        isSlideApproved() {
            return this.activeSlideNonEmptyWells.every(
                _well => _well.insemination && _well.insemination.isApproved
            )
        },
        noEmbryosHaveFertMethod() {
            return !this.activeSlideNonEmptyWells.some(
                _well => _well.insemination && _well.insemination.method
            )
        }
    },
    watch: {
        treatment: {
            handler(newVal) {
                if (newVal) {
                    this.setFormData(newVal)
                }
            },
            immediate: true
        }
    },
    methods: {
        setFormData(treatment) {
            this.form.collectedOocytes = treatment.collectedOocytes
            this.form.mii = treatment.mii
            this.form.inseminatedICSI = treatment.inseminatedICSI
            this.form.inseminatedIVF = treatment.inseminatedIVF
            this.form.degenerated = treatment.degenerated
        },
        toggleTreatmentFilter() {
            this.$store.commit('filter/setCycleDetailsVisibility', !this.cycleDetailsAreVisible)
        },
        updateTreatmentDetails(key) {
            if (this.form[key] && this.form[key] >= 0 && this.form[key] !== this.treatment[key]) {
                this.$store.dispatch('timelapse/updateTreatment', {
                    treatmentId: this.treatment._id,
                    payload: { [key]: this.form[key] }
                })
            } else {
                this.form[key] = this.treatment[key]
            }
        },
        async handleApproveButtonClick() {
            await this.$mainController.updateWellsFertApproval(this.activeSlideNonEmptyWells)

            const currentSlideIndex = this.treatment.slides.findIndex(
                _slide => _slide._id === this.selectedSlide._id
            )
            if (currentSlideIndex + 1 < this.treatment.slides.length) {
                this.selectSlide(this.treatment.slides[currentSlideIndex + 1])
            }
        },
        selectSlide(slide) {
            if (!this.selectedSlide || slide._id !== this.selectedSlide._id) {
                this.$store.commit('timelapse/setSelectedSlide', slide)
                this.$store.dispatch('video/slideChangeCleanUp')
            }
        },
        isSelected(slideId) {
            return this.selectedSlide && this.selectedSlide._id === slideId
        }
    }
}
</script>

<style lang="scss" scoped>
.slide-treatment-filter {
    height: 90vh;
    font-size: 0.876rem;

    @media screen and #{$new-break4close} {
        display: none;
    }
    .close-label {
        position: absolute;
        right: 2.5rem;
        margin-top: 0.8rem;
        cursor: pointer;
        z-index: 1;
    }
    .divider {
        padding-top: 3rem;
        position: absolute;
        width: 100%;
        border-bottom: 1px solid $gray;
    }
    .treatment-container {
        padding: 4rem 2rem 1.9rem 2.5rem;
        background-color: $dark-3;
        @include input-number-no-arrows();

        .treatment-methods {
            height: 24vh;
            padding: 1.5rem 0;
        }
        .treatment-input {
            display: flex;
            justify-content: space-between;
            padding: 0.2rem 0;

            .treatment-input-label {
                display: flex;
                align-items: center;
            }
        }
        .square-input-container {
            position: relative;
        }
        .square-input {
            width: 3rem;
            height: 2rem;
            background-color: transparent;
            color: $white;
            padding: 0.7rem;
            border: 1px solid $white;
            border-radius: 5px;
            box-sizing: border-box;
            outline: none;
            text-align: center;
        }
    }

    .insemination-container {
        padding: 1rem 2rem 1.9rem 2.5rem;
        background-color: $dark-2;
        height: 76vh;

        .slides-approved {
            padding: 1rem 0;
        }
        .insemination-method {
            border-bottom: 1px solid $gray;
            padding-bottom: 0.5rem;
        }
        .show-slide {
            padding: 1rem 0;
            .slideId {
                display: flex;
                justify-content: space-between;
            }
        }
        .insemination-instruction {
            color: $light;
            font-size: 0.75rem;
        }
        .indicator {
            display: flex;
            justify-content: center;
        }
        .slide-indicators {
            border: 1px solid $light;
            border-radius: 1rem;
            width: 3.5rem;
            height: 0.2rem;
            background-color: $light;
            margin: 1rem 0.2rem;
            cursor: pointer;

            &.currentSlide {
                border: 1px solid $blue;
                background-color: $blue;
            }
        }
        .approve-btn {
            text-align: center;
            margin: 1.5rem 0;
        }
    }
}
</style>
